import { useMemo } from 'react'
import { useAuth } from '@/lib/auth'
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canAccessNav,
  getAccessibleNavItems,
} from './permissions'

/**
 * Hook to check if user has a specific permission
 */
export function usePermission(permission: string): boolean {
  const { permissions, user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  
  return useMemo(
    () => hasPermission(permissions, permission, isSuperuser),
    [permissions, permission, isSuperuser]
  )
}

/**
 * Hook to check if user has any of the specified permissions
 */
export function useAnyPermission(permissionList: string[]): boolean {
  const { permissions, user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  
  return useMemo(
    () => hasAnyPermission(permissions, permissionList, isSuperuser),
    [permissions, permissionList, isSuperuser]
  )
}

/**
 * Hook to check if user has all of the specified permissions
 */
export function useAllPermissions(permissionList: string[]): boolean {
  const { permissions, user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  
  return useMemo(
    () => hasAllPermissions(permissions, permissionList, isSuperuser),
    [permissions, permissionList, isSuperuser]
  )
}

/**
 * Hook to check if user can access a navigation item
 */
export function useCanAccessNav(navItem: string): boolean {
  const { permissions, user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  
  return useMemo(
    () => canAccessNav(permissions, navItem, isSuperuser),
    [permissions, navItem, isSuperuser]
  )
}

/**
 * Hook to get list of accessible navigation items
 */
export function useAccessibleNavItems(): string[] {
  const { permissions, user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  
  return useMemo(
    () => getAccessibleNavItems(permissions, isSuperuser),
    [permissions, isSuperuser]
  )
}

/**
 * Hook to get multiple permission checks at once
 */
export function usePermissions<T extends string>(permissionList: readonly T[]): Record<T, boolean> {
  const { permissions, user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  
  return useMemo(() => {
    const result = {} as Record<T, boolean>
    for (const perm of permissionList) {
      result[perm] = hasPermission(permissions, perm, isSuperuser)
    }
    return result
  }, [permissions, permissionList, isSuperuser])
}
